import { IInstalledPackage, IEnduserBuildInfo } from "../state/deviceInstallerSlice";
import { DeviceFamily, isPlatformCompatible } from "./platformCompat";

export type BuildInstallStatus =
  | "not_connected"
  | "incompatible"
  | "install"
  | "installed"
  | "outdated"
  | "unknown";

interface BuildInstallStatusParams {
  isDeviceReady: boolean;
  deviceFamily: DeviceFamily | null;
  targetPlatform: string;
  packageName: string | null;
  buildVersion: number;
  installedPackages: Record<string, IInstalledPackage>;
}

/** Determine the install status of a build relative to the connected device */
export function getBuildInstallStatus({
  isDeviceReady,
  deviceFamily,
  targetPlatform,
  packageName,
  buildVersion,
  installedPackages,
}: BuildInstallStatusParams): BuildInstallStatus {
  if (!isDeviceReady) return "not_connected";
  if (deviceFamily && !isPlatformCompatible(targetPlatform, deviceFamily)) {
    return "incompatible";
  }
  if (!packageName) return "install";
  if (packageName in installedPackages) {
    const installed = installedPackages[packageName];
    if (installed.versionCode !== null && installed.versionCode < buildVersion) {
      return "outdated";
    }
    return "installed";
  }
  return "install";
}

interface EnduserBuildInput {
  packageName: string | null;
  targetPlatform: string;
  version: number | string;
  projectGroupName?: string;
  projectGroupId?: number;
  downloadUrl: string;
  id: number;
}

/** Map raw build data to IEnduserBuildInfo record keyed by packageName::platform */
export function mapToEnduserBuildInfos(
  builds: EnduserBuildInput[],
  fallbackProjectGroupName?: string,
): Record<string, IEnduserBuildInfo> {
  const buildMap: Record<string, IEnduserBuildInfo> = {};
  for (const b of builds) {
    if (!b.packageName) continue;
    const key = `${b.packageName}::${b.targetPlatform}`;
    buildMap[key] = {
      packageName: b.packageName,
      targetPlatform: b.targetPlatform,
      version: Number(b.version),
      projectGroupName: b.projectGroupName || fallbackProjectGroupName || `PG #${b.projectGroupId}`,
      s3Url: b.downloadUrl,
      buildId: b.id,
    };
  }
  return buildMap;
}
